// SWR hook for the master election-phase switch.
//
// Mirrors the existing electionday hooks (NOT useFeatureFlags, which
// is once-on-mount). The phase has to update live for readers who
// already have the page open when the newsroom flips it.
//
// Public-side: fetches /api/election-phase (edge-cached) every 30 s
// and on focus. Always returns a `phase`; on loading or error,
// defaults to 'normal' so the site falls back to the standard homepage.
//
// Admin-side can use the same hook for display, then call PUT
// /api/admin/election-phase + mutate() to refresh immediately after
// a successful write.

import useSWR, { type SWRConfiguration } from 'swr';
import {
  isElectionPhase,
  type ElectionPhase,
  type ElectionPhaseState,
} from '../data/electionPhase-types';

const URL = '/api/election-phase';
const FALLBACK_STATE: ElectionPhaseState = {
  phase: 'normal',
  updatedAt: new Date(0).toISOString(),
};

const fetcher = async (url: string): Promise<ElectionPhaseState> => {
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const body = (await res.json()) as Partial<ElectionPhaseState>;
  const phase: ElectionPhase = isElectionPhase(body.phase)
    ? body.phase
    : 'normal';
  const updatedAt =
    typeof body.updatedAt === 'string'
      ? body.updatedAt
      : new Date(0).toISOString();
  return { phase, updatedAt };
};

export interface UseElectionPhaseOptions {
  refreshInterval?: number;
}

export interface UseElectionPhaseResult {
  phase: ElectionPhase;
  updatedAt: string;
  isLoading: boolean;
  error: unknown;
  mutate: () => Promise<unknown>;
}

export function useElectionPhase(
  options: UseElectionPhaseOptions = {},
): UseElectionPhaseResult {
  const config: SWRConfiguration = {
    refreshInterval: options.refreshInterval ?? 30_000,
    revalidateOnFocus: true,
    errorRetryCount: 2,
  };
  const { data, isLoading, error, mutate } = useSWR<ElectionPhaseState>(
    URL,
    fetcher,
    config,
  );
  return {
    phase: data?.phase ?? FALLBACK_STATE.phase,
    updatedAt: data?.updatedAt ?? FALLBACK_STATE.updatedAt,
    isLoading,
    error,
    mutate,
  };
}
